import { FieldState, FieldValidator } from 'final-form';
import i18n from 'i18next';
import { getIsPhoneNumber, getIsValidPhone, Phone } from 'phoneNumberUtils';
import { Listing } from 'models/Listing';
import { BusinessInfoArrayField } from 'router/subrouters/ListingCreationWizard/common-pages/BusinessInfo/utils';
import utils, {
  doesValueExist,
  isEmail,
  isUrl,
  isUndefined,
  isName,
  safeParse,
  isLinkedinUrl,
} from 'utils';
import {
  countTextLength,
  getIsEditorValueEmpty,
} from 'components/RichTextEditorField/utils';
import { EMAIL_PHONE_INPUT_TYPE } from 'components/EmailPhoneInputField/EmailPhoneInputField.component';

// Final Form use undefined value for successful validation
export const VALID: any = undefined;

export const NUMBERS_ONLY = /^[1-9][0-9]{0,2}(,?[0-9]{3})*(\.[0-9]+)?$/i;
const DECIMAL_NUMBERS_ONLY =
  /^-?(0|[1-9][0-9]{0,2}(,?[0-9]{3})*)(\.[0-9]+)?([eE][-+]?[0-9]+)?$/;

const POSITIVE_NUMBERS_ONLY =
  /^(?:\d{1,3}(?:,\d{3})*|\d+)(?:\.\d+)?([eE][-+]?\d+)?$/;

const VALID_ACN = /^[0-9]{9}$/;
const VALID_ABN = /^[0-9]{11}$/;
const VALID_NZBN = /^[0-9]{13}$/;

const VALID_NON_PUBLIC_EMAIL_DOMAIN =
  /^@(?!(?:gmail|yahoo|icloud|hotmail|outlook)(\.|$))[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const isNonEmptyString = (value: any) => {
  return value.trim().length > 0;
};

export const checkboxRequiredTrue = (errorMessage?: string) => (value: any) =>
  value ? null : errorMessage || i18n.t('requiredField');

export const richEditorMaxLength =
  (maxLen: number, errorMessage?: string) => (value: any) => {
    if (!value) return VALID;

    const parsedValue = safeParse(value);
    const charCount = countTextLength(parsedValue);

    if (charCount > maxLen) {
      return (
        errorMessage ||
        i18n.t('charactersExceeded', { count: charCount - maxLen })
      );
    }

    return VALID;
  };

export const richEditorRequired = (errorMessage?: string) => (value: any) => {
  const returnMessage = errorMessage || i18n.t('requiredField');

  if (!doesValueExist(value)) {
    // undefined or null values are invalid
    return returnMessage;
  }

  if (getIsEditorValueEmpty(safeParse(value))) return returnMessage;

  return VALID;
};

export const required = (errorMessage?: string) => (value: any) => {
  const returnMessage = errorMessage || i18n.t('requiredField');

  if (!doesValueExist(value)) {
    // undefined or null values are invalid
    return returnMessage;
  }

  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : returnMessage;
  }

  return VALID;
};

export const minLength =
  (errorMessage: string, minLength: number) => (value: any) => {
    const hasLength = value && typeof value.length === 'number';
    return hasLength && value.length >= minLength ? VALID : errorMessage;
  };

export const maxLength =
  (errorMessage: string, maxLength: number) => (value: any) => {
    const hasLength = value && typeof value.length === 'number';
    return hasLength && value.length <= maxLength ? VALID : errorMessage;
  };

export const minNumber =
  (errorMessage: string, minNum: number) => (value: any) => {
    if (!value) return VALID;

    return parseInt(value) >= minNum ? VALID : errorMessage;
  };

export const maxNumber =
  (errorMessage: string, maxNum: number) => (value: any) => {
    if (!value) return VALID;

    return parseInt(
      String(value).includes(',') ? String(value).replace(/,/g, '') : value,
    ) <= maxNum
      ? VALID
      : errorMessage;
  };

export const email = (errorMsg?: string) => (value: string) => {
  if (!value) return VALID;

  return isEmail(value)
    ? VALID
    : errorMsg || i18n.t('pleaseEnterValidEmailAddress');
};

export const nonPublicEmailDomainValidation =
  (errorMsg: string) => (value: string) => {
    if (!value) return VALID;
    return value && VALID_NON_PUBLIC_EMAIL_DOMAIN.test(value)
      ? VALID
      : errorMsg;
  };

export const checkedFieldValidation =
  (errorMsg?: string) => (value: string) => {
    return value ? VALID : errorMsg || i18n.t('requiredField');
  };

export const contactNumber = (errorMessage?: string) => (value: string) => {
  if (!value) return;

  return getIsPhoneNumber(value)
    ? VALID
    : errorMessage || i18n.t('pleaseEnterValidPhoneNumber');
};

export const emailOrPhoneValidation =
  (errorMessage?: string) => (value: string) => {
    if (!value) return VALID;

    const trimmedValue = value.trim();
    const inputType = utils.getEmailPhoneInputType(trimmedValue);

    if (inputType === EMAIL_PHONE_INPUT_TYPE.EMAIL) {
      return isEmail(trimmedValue)
        ? VALID
        : errorMessage || i18n.t('pleaseEnterValidEmailAddress');
    }

    if (inputType === EMAIL_PHONE_INPUT_TYPE.PHONE) {
      return getIsPhoneNumber(trimmedValue)
        ? VALID
        : errorMessage || i18n.t('pleaseEnterValidPhoneNumber');
    }

    return errorMessage || i18n.t('pleaseEnterValidEmailOrPhone');
  };

export const numbersOnlyValidation =
  (errorMessage: string) => (value: string) => {
    if (!value) return;
    return NUMBERS_ONLY.test(value) ? VALID : errorMessage;
  };

export const validDecimalNumbersOnlyValidation =
  (errorMessage: string) => (value: string) => {
    if (!value) return;
    return DECIMAL_NUMBERS_ONLY.test(value) ? VALID : errorMessage;
  };

export const composeValidators =
  (...validators: FieldValidator<any>[]) =>
  (value: any, allValues: object, meta?: FieldState<any>) =>
    validators.reduce(
      (error, validator) => error || validator?.(value, allValues, meta),
      VALID,
    );

const passwordFieldNames = ['oldPassword', 'newPassword'];

export const passwordFieldsValidation = () => (value: any, allValues: any) =>
  !value &&
  passwordFieldNames.some((passwordType) => allValues[passwordType]) &&
  !passwordFieldNames.every((passwordType) => allValues[passwordType]) &&
  i18n.t('passwordValidationMessage');

export const isGreatherThanField =
  (otherFieldName: string) => (value: any, allValues: any) =>
    isUndefined(allValues[otherFieldName]) ||
    utils.getIntegerFromDottedNumber(value) >
      utils.getIntegerFromDottedNumber(allValues[otherFieldName])
      ? VALID
      : i18n.t('isGreaterThan', { otherFieldName: i18n.t(otherFieldName) });

export const isGreatherOrEqualThanField =
  (otherFieldName: string) => (value: any, allValues: any) =>
    utils.getIntegerFromDottedNumber(value) >=
    utils.getIntegerFromDottedNumber(allValues[otherFieldName])
      ? VALID
      : i18n.t('isGreaterThan', { otherFieldName: i18n.t(otherFieldName) });

export const isLowerThanField =
  (otherFieldName: string) => (value: any, allValues: any) =>
    utils.getIntegerFromDottedNumber(value) >
      utils.getIntegerFromDottedNumber(allValues[otherFieldName]) &&
    i18n.t('isLowerThan', { otherFieldName: i18n.t(otherFieldName) });

export const linkedinValidation = () => (value: string) =>
  value && !isLinkedinUrl(value) && i18n.t('linkedInErrorMessage');

export const greatherThanZeroOrZero =
  (errorMessage: string) => (value: number | string) => {
    if (value !== 0 && !value) return;
    if (value === 0 || +value === 0) return;

    return positiveNumbersOnly(errorMessage)(`${value}`);
  };

export const greatherThanZero =
  (errorMessage: string) => (value: number | string) => {
    if (value !== 0 && !value) return;
    if (value === 0 || +value === 0) return errorMessage;

    return positiveNumbersOnly(errorMessage)(`${value}`);
  };

export const positiveNumbersOnly =
  (errorMessage?: string) => (value: string) => {
    if (!value) return;
    return POSITIVE_NUMBERS_ONLY.test(value)
      ? VALID
      : errorMessage || i18n.t('onlyPositiveNumbersAreAllowed');
  };

export const exactLength =
  (length: number, errorMessage: string) => (value: any) => {
    if (!value) return;
    return String(value).length === length ? VALID : errorMessage;
  };

export const url = (errorMessage: string) => (value: any) => {
  if (!value) return;
  return isUrl(value) ? VALID : errorMessage;
};

export const onlyFuture = (errorMessage: string) => (value: Date) => {
  if (!value) return;
  const currentDate = new Date();
  return currentDate.getTime() <= new Date(value).getTime()
    ? VALID
    : errorMessage;
};

export const onlyPast = (errorMessage: string) => (value: Date) => {
  if (!value) return;
  const currentDate = new Date();
  currentDate.setUTCHours(23, 59, 59, 999);

  return new Date(value).getTime() <= currentDate.getTime()
    ? VALID
    : errorMessage;
};

export const phoneRequired = (errorMessage?: string) => (value: Phone) => {
  if (value?.countryCode && value?.phoneNumber) return VALID;

  return errorMessage || i18n.t('requiredField');
};

export const phone = (errorMessage?: string) => (value: Phone) => {
  if (!value?.phoneNumber) return VALID;

  return getIsValidPhone(value)
    ? VALID
    : errorMessage || i18n.t('notValidPhoneNumber');
};

export const dateValidator =
  (index: number, key: string) =>
  (value: any, allValues: Record<string, any>) => {
    if (allValues['deadline'][index]?.[key]) return VALID;

    if (!doesValueExist(value)) return i18n.t('requiredField');

    return value ? VALID : i18n.t('requiredField');
  };

export const noLongerThan =
  (length: number, message: string) => (value: any) => {
    if (!value || !value?.length) return VALID;

    return value.length > length ? message : VALID;
  };

export const noShorterThan =
  (length: number, message: string) => (value: any) => {
    if (!value || !value?.length) return VALID;

    return value.length < length ? message : VALID;
  };

export const requireOneFromArray = (errorMessage?: string) => (value: any) => {
  const returnMessage = errorMessage || i18n.t('noteThat2Required');

  if (value.every(isUndefined)) return returnMessage;

  return VALID;
};

const numberOfEmptyArrayFields = (values: BusinessInfoArrayField) => {
  return Object.entries(values).reduce((acc, [key, value]) => {
    return value.every(isUndefined) ? ++acc : acc;
  }, 0);
};

export const requireBuisinessInfoArrays =
  (errorMessage?: string) => (value: any, allValues: Partial<Listing>) => {
    const { highlights, keyAssets, products } = allValues;
    return numberOfEmptyArrayFields({
      highlights,
      keyAssets,
      products,
    } as BusinessInfoArrayField) > 1
      ? requireOneFromArray(errorMessage)(value)
      : VALID;
  };

export const fullNameValidation = (message: string) => (value: string) => {
  if (!value) return VALID;

  if (!isName(value)) {
    return message;
  }

  return VALID;
};

export const acnField = (errorMsg: string) => (value: string) => {
  if (!value) return VALID;
  return VALID_ACN.test(value) ? VALID : errorMsg;
};

export const abnField = (errorMsg: string) => (value: string) => {
  if (!value) return VALID;
  return VALID_ABN.test(value) ? VALID : errorMsg;
};

export const nzbnField = (errorMsg: string) => (value: string) => {
  if (!value) return VALID;
  return VALID_NZBN.test(value) ? VALID : errorMsg;
};

export const maxChars =
  (maxChars: number, errorMsg?: string) => (value: string) => {
    if (!value || value.length <= maxChars) return VALID;

    return (
      errorMsg || i18n.t('maxNumberOfCharachersReacted', { count: maxChars })
    );
  };

export const conditionalRequired = (isRequired: boolean) => (value: string) => {
  if (isRequired) return required()(value);
  return VALID;
};

export const getFieldValidations = (
  required: boolean,
  otherValidations: FieldValidator<any>[] = [],
) => ({
  required,
  validate: composeValidators(
    conditionalRequired(required),
    ...otherValidations,
  ),
});
